/**
 * /dashboard/welcome — Voice-first onboarding.
 *
 * iter v29 r080 · "Voice as primary input"
 * - Solo quando l'agente non ha ancora creato annunci (count 0).
 * - Mobile-detect: bottone microfono gigante al centro (è il device dove
 *   parlare è naturale). Desktop: idem, ma offre più "trapanati" via tastiera.
 * - Soft fallback: "Preferisco compilare a mano" → /dashboard/property/new.
 *
 * Flusso:
 *   parla → backend trascrive + estrae campi → POST /properties → nav cantiere.
 *
 * Riusa VoiceToFormModal in modalità create.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Microphone, ArrowRight, Keyboard, Sparkle, Lightning } from "@phosphor-icons/react";

import AppShell from "@/components/AppShell";
import VoiceToFormModal from "@/components/VoiceToFormModal";
import { api } from "@/lib/api";
import { toast } from "sonner";

const isMobile = () => {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) || window.innerWidth < 768;
};

export default function VoiceWelcome() {
  const nav = useNavigate();
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  // iter v29 r107 — Mini-tour 3 step prima del microfono, solo la prima
  // volta (localStorage flag). Risolve il blocco utente che non sa cosa
  // dire e si trova davanti a un microfono senza contesto.
  const [showTour, setShowTour] = useState(() => {
    try { return !localStorage.getItem("pronto_welcome_tour_done"); }
    catch { return false; }
  });
  const [tourStep, setTourStep] = useState(0);
  const tourSteps = [
    {
      title: "Benvenuto in Pronto",
      body: "Qui dentro fai tutto a voce. Parli un minuto e Pronto ti monta annuncio, foto, pianta arredata e post social.",
      cta: "Avanti",
    },
    {
      title: "Cosa puoi dire",
      body: "Indirizzo, metri quadri, prezzo, stanze, pregi e difetti. Come parli al cliente al telefono — non serve nessun formato preciso.",
      cta: "Avanti",
    },
    {
      title: "Quando hai finito",
      body: "Puoi sempre correggere qualsiasi campo a voce o a mano. Niente è definitivo, niente è obbligatorio.",
      cta: "Inizia",
    },
  ];

  const closeTour = () => {
    try { localStorage.setItem("pronto_welcome_tour_done", "1"); } catch {}
    setShowTour(false);
  };
  const mobile = useMemo(isMobile, []);

  // Hot-link: ?open=1 forza apertura microfono al mount (es. da CTA email)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("open") === "1") {
      // piccolo delay per evitare problemi se utente non ha ancora cliccato in pagina
      setTimeout(() => setVoiceOpen(true), 400);
    }
  }, []);

  /**
   * onApply del modal vocale: l'AI ha estratto i campi → creiamo direttamente
   * l'immobile e portiamo l'agente al cantiere. Niente form intermedio.
   */
  const handleVoiceApply = async (fields /* , transcript, opts */) => {
    setVoiceOpen(false);
    setCreating(true);

    // Costruzione titolo automatico se assente
    const guessTitle = () => {
      if (fields.title) return fields.title.trim();
      const parts = [];
      if (fields.property_type) parts.push(fields.property_type);
      if (fields.surface) parts.push(`${fields.surface} m²`);
      if (fields.city) parts.push(fields.city);
      return parts.length ? parts.join(" · ") : "Nuovo annuncio";
    };

    try {
      const payload = {
        title: guessTitle(),
        listing_type: fields.listing_type || "vendita",
        property_type: fields.property_type || "Appartamento",
        price: fields.price ? Number(fields.price) : null,
        surface: fields.surface ? Number(fields.surface) : null,
        rooms: fields.rooms ? parseInt(fields.rooms, 10) : null,
        bathrooms: fields.bathrooms ? parseInt(fields.bathrooms, 10) : null,
        floor: fields.floor || null,
        address: fields.address || null,
        city: fields.city || null,
        features: fields.features
          ? String(fields.features).split(",").map((s) => s.trim()).filter(Boolean)
          : [],
        notes: fields.notes || null,
      };
      const { data } = await api.post("/properties", payload);
      toast.success("Annuncio creato dalla tua voce");
      nav(`/dashboard/property/${data.id}?tab=foto&from=welcome`);
    } catch (err) {
      console.error("voice-welcome create failed", err);
      toast.error(err?.response?.data?.detail || "Non sono riuscito a creare l'annuncio. Riprova o compila a mano.");
      setCreating(false);
    }
  };

  return (
    <AppShell title="Benvenuto" breadcrumb="Inizia">
      <div
        className="relative mx-auto max-w-[640px] py-10 sm:py-16 px-2"
        data-testid="voice-welcome"
      >
        {/* Etichetta in alto */}
        <div className="flex items-center gap-2 mb-3" data-testid="welcome-overline">
          <Sparkle size={14} weight="fill" className="text-[#B57C5E]" />
          <span className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#7C8B6E]">
            Iniziamo
          </span>
        </div>

        <h1
          className="font-editorial text-[34px] sm:text-[42px] leading-[1.07] tracking-tight text-[#1C1C1A]"
          data-testid="welcome-title"
        >
          Parlami della casa<br />
          <span className="italic-accent text-[#7C8B6E]">che stai vendendo.</span>
        </h1>

        <p className="mt-5 text-[15px] text-[#6B6A65] leading-relaxed max-w-[520px]" data-testid="welcome-sub">
          Indirizzo, prezzo, stanze, pregi e difetti. Parla come parli al cliente al telefono — Pronto trascrive, riempie i campi e ti porta direttamente al cantiere dell'annuncio.
        </p>

        {/* Bullet rapidi · cosa succede */}
        <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="welcome-bullets">
          {[
            { label: "Parli 30 secondi", caption: "Una descrizione naturale, niente form." },
            { label: "Pronto compila", caption: "Indirizzo, prezzo, m², caratteristiche." },
            { label: "Sei al cantiere", caption: "Foto, annuncio, vetrina · tutto in una pagina." },
          ].map((b, i) => (
            <div
              key={i}
              className="rounded-lg border border-[#E5E7E0] bg-white p-3"
              data-testid={`welcome-bullet-${i}`}
            >
              <div className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-[#7C8B6E]/12 text-[#5A6A4D] text-[10px] font-bold flex items-center justify-center">
                  {i + 1}
                </span>
                <span className="text-[12.5px] font-semibold text-[#1C1C1A]">{b.label}</span>
              </div>
              <div className="text-[11.5px] text-[#6B6A65] mt-1.5 leading-snug">{b.caption}</div>
            </div>
          ))}
        </div>

        {/* CTA principale · microfono centrale */}
        <button
          type="button"
          onClick={() => setVoiceOpen(true)}
          disabled={creating}
          className="mt-10 w-full group relative overflow-hidden rounded-2xl bg-[#1C1C1A] text-white px-6 py-7 sm:py-8 shadow-[0_24px_50px_-18px_rgba(28,28,26,0.45)] hover:shadow-[0_28px_60px_-16px_rgba(124,139,110,0.45)] transition-all duration-300 disabled:opacity-60 disabled:cursor-wait"
          data-testid="welcome-mic-btn"
        >
          {/* glow */}
          <span className="absolute inset-0 bg-gradient-to-br from-[#7C8B6E]/30 via-transparent to-[#B57C5E]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <div className="relative flex items-center justify-center gap-4">
            <span className="w-16 h-16 rounded-full bg-[#7C8B6E] flex items-center justify-center shrink-0 ring-4 ring-[#7C8B6E]/25 group-hover:scale-105 transition-transform">
              <Microphone size={28} weight="fill" className="text-white" />
            </span>
            <div className="text-left">
              <div className="text-[19px] sm:text-[22px] font-semibold tracking-tight leading-tight">
                {creating ? "Sto creando l'annuncio…" : "Tocca per parlare"}
              </div>
              <div className="text-[12.5px] text-white/70 mt-1">
                {mobile ? "Usa il microfono del telefono" : "Usa il microfono del computer"} · al massimo 1 minuto
              </div>
            </div>
          </div>
        </button>

        {/* Soft fallback · compila a mano */}
        <div className="mt-5 flex items-center justify-center gap-2 text-[#6B6A65] text-[13px]" data-testid="welcome-fallback">
          <Keyboard size={14} />
          <button
            type="button"
            onClick={() => nav("/dashboard/property/new")}
            className="underline underline-offset-2 hover:text-[#1C1C1A] transition-colors"
            data-testid="welcome-skip-btn"
          >
            Preferisco compilare a mano
          </button>
        </div>

        {/* Mini-tip in basso · cosa potrai modificare dopo */}
        <div className="mt-10 flex items-start gap-3 rounded-xl bg-[#F1F4ED] border border-[#7C8B6E]/20 px-4 py-3.5" data-testid="welcome-tip">
          <Lightning size={18} weight="fill" className="text-[#7C8B6E] mt-0.5 shrink-0" />
          <div className="text-[12.5px] text-[#3F3D38] leading-relaxed">
            Se sbaglio qualcosa, lo correggi a voce o a mano direttamente dal cantiere dell'annuncio. Nessun campo è definitivo.
          </div>
        </div>
      </div>

      {/* Modal vocale · riuso del componente già esistente */}
      <VoiceToFormModal
        open={voiceOpen}
        onClose={() => setVoiceOpen(false)}
        onApply={handleVoiceApply}
        existingFields={{}}
        allowOverwrite={true}
        mode="create"
      />

      {/* iter v29 r107 — Tour 3 step mostrato solo la prima volta.
          Risolve il blocco "non so cosa dire" davanti al microfono nudo. */}
      {showTour && (
        <div
          className="fixed inset-0 z-[150] bg-[#1C1C1A]/65 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          data-testid="welcome-tour-overlay"
        >
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="p-6 sm:p-7">
              <div className="flex items-center gap-1.5 mb-4">
                {tourSteps.map((_, i) => (
                  <div
                    key={i}
                    className={`h-1 rounded-full transition-all ${
                      i === tourStep ? "w-8 bg-[#7C8B6E]" : "w-2 bg-[#E5E7E0]"
                    }`}
                  />
                ))}
              </div>
              <h2 className="text-[22px] font-serif text-[#1C1C1A] leading-tight">
                {tourSteps[tourStep].title}
              </h2>
              <p className="mt-3 text-[14px] text-[#6B6A65] leading-relaxed">
                {tourSteps[tourStep].body}
              </p>
              <div className="mt-6 flex items-center justify-between gap-3">
                <button
                  onClick={closeTour}
                  className="text-[12.5px] text-[#9A968B] hover:text-[#1C1C1A] underline underline-offset-2"
                  data-testid="welcome-tour-skip"
                >
                  Salta
                </button>
                <button
                  onClick={() => {
                    if (tourStep < tourSteps.length - 1) {
                      setTourStep(tourStep + 1);
                    } else {
                      closeTour();
                    }
                  }}
                  className="bg-[#1C1C1A] hover:bg-black text-white text-[13px] font-semibold px-5 py-2.5 rounded-lg transition-colors"
                  data-testid="welcome-tour-next"
                >
                  {tourSteps[tourStep].cta}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
